import React, { useState, useEffect } from 'react';
import { Search, ArrowRight } from 'lucide-react';
import { getDeals } from '../../api/dashboard';

export default function DealFinder({ onCardClick }: { onCardClick?: (id: string) => void }) {
  const [deals, setDeals] = useState<any[]>([]);
  const [minMargin, setMinMargin] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDeals(minMargin, 30)
      .then(d => setDeals(d.deals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [minMargin]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Deal Finder</h2>
          <p className="text-sm text-neutral-500 mt-1">Ecarts de prix entre les marketplaces</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-400">Marge min:</span>
          {[5, 10, 20, 50].map(m => (
            <button key={m} onClick={() => setMinMargin(m)}
              className={`px-3 py-1.5 rounded-lg text-sm ${minMargin === m ? 'bg-accent text-white' : 'bg-surface text-neutral-400 hover:bg-neutral-800'}`}>
              {m}%
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-neutral-500">Recherche des deals...</div>
      ) : deals.length === 0 ? (
        <div className="text-center py-20 text-neutral-500">Aucun deal trouvé avec {minMargin}% de marge minimum</div>
      ) : (
        <div className="grid gap-4">
          {deals.map((deal, i) => {
            const bestMargin = Math.max(
              Number(deal.margin_fr_vs_eu) || 0,
              Number(deal.margin_ebay_vs_cm) || 0
            );
            const marginType = (Number(deal.margin_fr_vs_eu) || 0) > (Number(deal.margin_ebay_vs_cm) || 0)
              ? 'CM FR < CM EU' : 'eBay FR > CM';

            return (
              <div key={`${deal.id}-${i}`} onClick={() => onCardClick?.(deal.id)} className="bg-surface border border-border rounded-2xl p-5 hover:border-accent/50 transition-colors cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img src={`${process.env.REACT_APP_API_URL || ''}/img/opus-assets/cards/${deal.id}.png`} alt="" className="w-10 h-14 rounded flex-shrink-0 object-cover bg-neutral-700" />
                    <div>
                      <div className="text-sm font-medium text-white">{deal.name_fr || deal.name}</div>
                      <div className="text-xs text-neutral-500">{deal.id} — {deal.rarity}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-400">+{bestMargin.toFixed(1)}%</div>
                    <div className="text-xs text-neutral-500">{marginType}</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-neutral-500">CM EU</div>
                    <div className="text-white font-medium">{deal.cm_eur ? `${Number(deal.cm_eur).toFixed(2)}€` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">CM FR</div>
                    <div className="text-white font-medium">{deal.fr_low ? `${Number(deal.fr_low).toFixed(2)}€` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">TCG USD</div>
                    <div className="text-white font-medium">{deal.tcg_usd ? `$${Number(deal.tcg_usd).toFixed(2)}` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">eBay USD</div>
                    <div className="text-white font-medium">{deal.ebay_usd ? `$${Number(deal.ebay_usd).toFixed(2)}` : '—'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
